import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/ui/input";
import { Button } from "@/ui/button";
import { Loader } from "smart-react";
import { Eye, MoreVertical } from "lucide-react";

export type SortDir = "asc" | "desc";

export type ListFetchParams = {
  page: number; // 1-based
  pageSize: number;
  search?: string;
  sort?: { field: string; dir: SortDir } | null;
  filters?: Record<string, unknown>;
};

export type ListFetchResult<T> = {
  items: T[];
  total: number;
};

export type SelectionMode = "none" | "single" | "multiple";

export type ListViewProps<T> = {
  idKey: keyof T & string;
  fetchList: (params: ListFetchParams) => Promise<ListFetchResult<T>>;
  itemRenderer: (item: T) => React.ReactNode;

  // Optional
  className?: string;
  filterBar?: React.ReactNode;
  selectionMode?: SelectionMode;
  onItemClick?: (item: T) => void;
  onAdd?: () => void;
  onDelete?: (selected: T[]) => Promise<void> | void;
  initialSearch?: string;
  initialSort?: { field: string; dir: SortDir } | null;
  pageSizeOptions?: number[];
  defaultPageSize?: number;

  // New customization
  hideSearch?: boolean;
  onView?: (item: T) => void;
  viewLabel?: string;
  actionVariant?: "button" | "kebab";
  itemActions?: (item: T) => React.ReactNode;
  extraToolbarRight?:
    | React.ReactNode
    | ((ctx: {
        selectedCount: number;
        onDelete: () => void;
        search: string;
        setSearch: (v: string) => void;
      }) => React.ReactNode);
  confirmTitle?: string;
  confirmMessage?: string;
  confirmOkLabel?: string;
  confirmCancelLabel?: string;
  externalFilters?: Record<string, unknown>;

  // Selection UX controls
  showCheckboxes?: boolean; // default: true
  selectOnRowClick?: boolean; // default: false
};

export function ListView<T>(props: ListViewProps<T>) {
  const {
    idKey,
    fetchList,
    itemRenderer,
    className,
    filterBar,
    selectionMode = "none",
    onItemClick,
    onAdd,
    onDelete,
    initialSearch = "",
    initialSort = null,
    pageSizeOptions = [10, 20, 50],
    defaultPageSize = 10,
    hideSearch = false,
    extraToolbarRight,
    externalFilters,
    showCheckboxes = true,
    selectOnRowClick = false,
    onView,
    viewLabel,
    actionVariant = "button",
    itemActions,
  } = props;

  const [items, setItems] = React.useState<T[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const [search, setSearch] = React.useState(initialSearch);
  const [sort, setSort] = React.useState<{
    field: string;
    dir: SortDir;
  } | null>(initialSort);
  const [filters, setFilters] = React.useState<
    Record<string, unknown> | undefined
  >(undefined);

  const [selectedMap, setSelectedMap] = React.useState<
    Record<string | number, T>
  >({});
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const selectedItems = React.useMemo(
    () => Object.values(selectedMap),
    [selectedMap]
  );

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchList({
        page,
        pageSize,
        search: search?.trim() || undefined,
        sort,
        filters: externalFilters ?? filters,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } finally {
      setLoading(false);
    }
  }, [fetchList, page, pageSize, search, sort, filters]);

  React.useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, sort]);

  // Selection handlers
  const toggleSelect = (item: T) => {
    if (selectionMode === "none") return;
    const id = (item as any)[idKey] as string | number;
    setSelectedMap((prev) => {
      if (selectionMode === "single") {
        return prev[id] ? {} : { [id]: item };
      }
      // multiple
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = item;
      }
      return next;
    });
  };

  const selectAllOnPage = () => {
    if (selectionMode !== "multiple") return;
    const next: Record<string | number, T> = { ...selectedMap };
    for (const it of items) {
      const id = (it as any)[idKey] as string | number;
      next[id] = it;
    }
    setSelectedMap(next);
  };

  const clearSelection = () => setSelectedMap({});

  // Standardized kebab actions for "View" (unified styling across lists)
  const Kebab: React.FC<{ onView: () => void }> = ({ onView }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (!open) return;
      const onDoc = (e: MouseEvent) => {
        const t = e.target as Node;
        if (ref.current && !ref.current.contains(t)) setOpen(false);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDoc);
        document.removeEventListener("keydown", onKey);
      };
    }, [open]);

    return (
      <div ref={ref} className="relative">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "group h-8 w-8 rounded-lg bg-muted/40 ring-1 ring-border/60",
            "hover:bg-accent/80 hover:text-accent-foreground hover:ring-ring/50",
            "transition-colors"
          )}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-label="Actions"
        >
          <MoreVertical className="h-4 w-4 text-muted-foreground group-hover:text-accent-foreground" />
        </Button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-2 w-40 rounded-md border border-border bg-popover text-popover-foreground shadow-md z-50 p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                onView();
                setOpen(false);
              }}
            >
              {viewLabel || "View Details"}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Toolbar actions
  const handleDelete = async () => {
    if (!onDelete || selectedItems.length === 0) return;
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!onDelete || selectedItems.length === 0) {
      setConfirmOpen(false);
      return;
    }
    setDeleting(true);
    try {
      await onDelete(selectedItems);
      clearSelection();
      await refresh();
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  const isRightFn = typeof extraToolbarRight === "function";
  const rightNode =
    typeof extraToolbarRight === "function"
      ? (
          extraToolbarRight as (ctx: {
            selectedCount: number;
            onDelete: () => void;
            search: string;
            setSearch: (v: string) => void;
          }) => React.ReactNode
        )({
          selectedCount: selectedItems.length,
          onDelete: handleDelete,
          search,
          setSearch: (v: string) => {
            setPage(1);
            setSearch(v);
          },
        })
      : extraToolbarRight;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {onAdd && (
            <Button type="button" onClick={onAdd}>
              Add
            </Button>
          )}
          {onDelete && selectionMode !== "none" && !isRightFn && (
            <Button
              type="button"
              variant="destructive"
              disabled={selectedItems.length === 0}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!hideSearch && (
            <div className="transition-all duration-200 w-48 sm:w-64 focus-within:w-72 sm:focus-within:w-96">
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch((e.target as HTMLInputElement).value);
                }}
                className="h-9 w-full"
              />
            </div>
          )}
          {rightNode}
        </div>
      </div>

      {/* Filters area */}
      {filterBar && <>{filterBar}</>}

      {/* List body */}
      <div>
        {loading ? (
          <div className="p-6 flex items-center justify-center">
            <Loader />
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            No records found.
          </div>
        ) : (
          <ul className="space-y-2 px-0 md:px-2">
            {items.map((it) => {
              const id = (it as any)[idKey] as string | number;
              const selected = !!selectedMap[id];
              return (
                <li
                  key={String(id)}
                  className={cn(
                    "rounded-lg border border-border bg-background px-4 py-3 hover:shadow-sm flex items-start gap-3",
                    selected && "ring-1 ring-ring",
                    (onItemClick ||
                      (selectOnRowClick && selectionMode !== "none")) &&
                      "cursor-pointer"
                  )}
                  onClick={() => {
                    if (selectOnRowClick && selectionMode !== "none") {
                      toggleSelect(it);
                    } else {
                      onItemClick?.(it);
                    }
                  }}
                >
                  {selectionMode !== "none" && showCheckboxes && (
                    <input
                      type="checkbox"
                      className="self-center h-4 w-4 border border-border rounded accent-blue-600"
                      style={{ appearance: "auto" }}
                      checked={selected}
                      onChange={() => toggleSelect(it)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="flex-1">{itemRenderer(it)}</div>
                  {(itemActions || onView) && (
                    <div className="shrink-0">
                      {itemActions
                        ? itemActions(it)
                        : onView &&
                          (actionVariant === "kebab" ? (
                            <Kebab onView={() => onView(it)} />
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 px-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                onView?.(it);
                              }}
                              title={viewLabel || "View Details"}
                            >
                              <Eye className="h-3.5 w-3.5 mr-1" />
                              {viewLabel || "View Details"}
                            </Button>
                          ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirmOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => !deleting && setConfirmOpen(false)}
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="lv-confirm-title"
            aria-describedby="lv-confirm-desc"
          >
            <div
              className="w-full max-w-sm rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-border">
                <h2 id="lv-confirm-title" className="text-base font-semibold">
                  {props.confirmTitle || "Delete selected?"}
                </h2>
              </div>
              <div className="px-4 py-3">
                <p
                  id="lv-confirm-desc"
                  className="text-sm text-muted-foreground"
                >
                  {props.confirmMessage ||
                    `Delete ${selectedItems.length} selected item(s)?`}
                </p>
              </div>
              <div className="px-4 py-3 flex justify-end gap-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmOpen(false)}
                  disabled={deleting}
                >
                  {props.confirmCancelLabel || "Cancel"}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {props.confirmOkLabel || "Delete"}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Footer: pagination */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          <span>·</span>
          <span>Total {total}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="h-9 rounded-md border border-border bg-input px-2 text-sm text-foreground"
            value={pageSize}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setPageSize(v);
              setPage(1);
            }}
          >
            {pageSizeOptions.map((ps) => (
              <option key={ps} value={ps}>
                {ps} / page
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ListView;
